import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, type ImportPreview, type ImportResult, type SessionCandidate, type SessionView } from '../../api';

type Row = { attended: '' | 'yes' | 'no'; grade: string; comment: string };

/**
 * Taking the register for a live session.
 *
 * Present completes the unit; absent fails it. Nothing is saved for a person
 * left blank, so a half-taken register can be finished later without wrongly
 * marking the people who were not reached yet.
 */
export function SessionRegister() {
  const { id } = useParams();
  const [view, setView] = useState<SessionView | null>(null);
  const [rows, setRows] = useState<Record<number, Row>>({});
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState<SessionCandidate[]>([]);
  const [adding, setAdding] = useState('');

  const load = useCallback(async () => {
    try {
      const [v, c] = await Promise.all([
        api.get<SessionView>(`/instructor/sessions/${id}`),
        api.get<{ candidates: SessionCandidate[] }>(`/instructor/sessions/${id}/candidates`),
      ]);
      setCandidates(c.candidates);
      setView(v);
      setRows(Object.fromEntries(v.people.map((p) => [p.userId, {
        attended: p.attended == null ? '' : p.attended ? 'yes' : 'no',
        grade: p.grade == null ? '' : String(p.grade),
        comment: p.comment ?? '',
      }])) as Record<number, Row>);
    } catch (e) { setError((e as ApiError).message); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  if (error && !view) return <p className="auth__error">{error}</p>;
  if (!view) return <p className="muted">Loading…</p>;

  const marks = Object.entries(rows)
    .filter(([, r]) => r.attended !== '')
    .map(([userId, r]) => ({
      userId: Number(userId),
      attended: r.attended === 'yes',
      grade: r.grade.trim() === '' ? null : Number(r.grade),
      comment: r.comment.trim() || undefined,
    }));

  const save = async () => {
    setBusy(true); setError(null); setNote(null);
    try {
      const r = await api.post<{ marked: number }>(`/instructor/sessions/${id}/attendance`, { marks });
      setNote(`${r.marked} ${r.marked === 1 ? 'person' : 'people'} marked. Their course records have been updated.`);
      await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const add = async () => {
    if (!adding) return;
    setBusy(true); setError(null); setNote(null);
    try {
      await api.post(`/instructor/sessions/${id}/people`, { userId: Number(adding) });
      setAdding(''); setNote('Added to the register.'); await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const drop = async (userId: number, name: string) => {
    if (!confirm(`Take ${name} off this class?`)) return;
    setBusy(true); setError(null); setNote(null);
    try {
      await api.del(`/instructor/sessions/${id}/people/${userId}`);
      setNote('Taken off the register.'); await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <nav className="crumbs">
        <Link to="/marking">To mark</Link> <span aria-hidden>›</span> {view.session.name}
      </nav>

      <div className="page-head">
        <h1 className="h1">{view.session.name}</h1>
        <span className="muted">
          {view.course.name} · {new Date(view.session.startsAt).toLocaleString('en-GB', {
            weekday: 'short', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
          })} · {view.session.durationMinutes} min
          {view.session.location ? ` · ${view.session.location}` : ''}
        </span>
      </div>

      {note && <p className="notice">{note}</p>}
      {error && <p className="auth__error">{error}</p>}

      <AttendanceImport sessionId={Number(id)} onDone={(m) => { setNote(m); void load(); }} />

      <div className="filters">
        <select value={adding} onChange={(e) => setAdding(e.target.value)} aria-label="Add a trainee">
          <option value="">Add somebody to this class…</option>
          {candidates.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.firstName} {c.lastName} ({c.login})
            </option>
          ))}
        </select>
        <button className="btn btn--ghost btn--small" disabled={busy || !adding} onClick={() => void add()}>Add</button>
        {candidates.length === 0 && <span className="muted small">Everyone on the course already has a place.</span>}
      </div>

      {view.people.length === 0
        ? <p className="muted">Nobody has signed up for this class yet.</p>
        : (
          <>
            <table className="grid">
              <thead>
                <tr><th>Trainee</th><th>Attended</th><th>Mark (optional)</th><th>Note (optional)</th><th /></tr>
              </thead>
              <tbody>
                {view.people.map((p) => (
                  <tr key={p.userId}>
                    <td>{p.firstName} {p.lastName} <span className="muted small mono">{p.login}</span></td>
                    <td>
                      <select value={rows[p.userId]?.attended ?? ''} aria-label={`Attendance for ${p.firstName}`}
                        onChange={(e) => setRows({ ...rows, [p.userId]: { ...rows[p.userId], attended: e.target.value as Row['attended'] } })}>
                        <option value="">—</option>
                        <option value="yes">Present</option>
                        <option value="no">Absent</option>
                      </select>
                    </td>
                    <td>
                      <input type="number" min={0} max={100} className="narrow" aria-label={`Mark for ${p.firstName}`}
                        value={rows[p.userId]?.grade ?? ''}
                        onChange={(e) => setRows({ ...rows, [p.userId]: { ...rows[p.userId], grade: e.target.value } })} />
                    </td>
                    <td>
                      <input aria-label={`Note about ${p.firstName}`}
                        value={rows[p.userId]?.comment ?? ''}
                        onChange={(e) => setRows({ ...rows, [p.userId]: { ...rows[p.userId], comment: e.target.value } })} />
                    </td>
                    <td>
                      {p.attended === null
                        ? <button className="link-danger" disabled={busy}
                            onClick={() => void drop(p.userId, `${p.firstName} ${p.lastName ?? ''}`.trim())}>remove</button>
                        : <span className="muted small">marked</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="row">
              <button className="btn btn--primary" disabled={busy || marks.length === 0} onClick={() => void save()}>
                {busy ? 'Saving…' : `Save register (${marks.length})`}
              </button>
              <span className="muted small">
                Present completes the unit for that trainee; absent fails it. Anyone left on “—” is not touched.
              </span>
            </div>
          </>
        )}
    </>
  );
}

/**
 * Taking the register from the file Zoom or Teams exports, rather than ticking
 * two hundred boxes. It always shows what it found first: a register decides
 * whether somebody's course completes, so it is not written on a guess.
 */
/**
 * Read the exported file as text.
 *
 * Teams writes its attendance report in UTF-16 with a byte order mark, and
 * File.text() assumes UTF-8, which turns every name into rubbish. The mark at
 * the front of the file says which encoding it is, so read the bytes and
 * decode accordingly rather than trusting the name or the extension.
 */
async function readReport(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const encoding =
    bytes[0] === 0xff && bytes[1] === 0xfe ? 'utf-16le'
    : bytes[0] === 0xfe && bytes[1] === 0xff ? 'utf-16be'
    : 'utf-8';
  try {
    return new TextDecoder(encoding).decode(bytes);
  } catch {
    return file.text();
  }
}

function AttendanceImport({ sessionId, onDone }: { sessionId: number; onDone: (note: string) => void }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [minimumMinutes, setMinimum] = useState('0');
  const [markOthersAbsent, setMarkAbsent] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const body = () => ({
    csv,
    minimumMinutes: Number(minimumMinutes) || 0,
    markOthersAbsent,
  });

  const look = async () => {
    setBusy(true); setError(null); setPreview(null);
    try { setPreview(await api.post<ImportPreview>(`/instructor/sessions/${sessionId}/attendance/import`, body())); }
    catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true); setError(null);
    try {
      const out = await api.post<ImportResult>(
        `/instructor/sessions/${sessionId}/attendance/import`, { ...body(), confirm: true },
      );
      onDone(`Register taken: ${out.present} present${out.absent ? `, ${out.absent} absent` : ''}.`);
      setOpen(false); setPreview(null); setCsv(''); setFileName('');
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <p>
        <button className="btn btn--ghost" onClick={() => setOpen(true)}>
          Take the register from a Zoom or Teams file
        </button>
      </p>
    );
  }

  return (
    <section className="panel">
      <div className="page-head">
        <h2 className="h2">Take the register from the meeting</h2>
        <button className="btn btn--ghost" onClick={() => { setOpen(false); setPreview(null); }}>Cancel</button>
      </div>

      <p className="muted small">
        In Teams, open the meeting chat after the class has ended and use
        <em> Attendance → Download</em>. In Zoom it is <em>Reports → Usage → Participants → Export</em>.
        Save the file and choose it here — it is read as exported, whatever it is named.
      </p>

      <div className="form">
        <label><span>The exported file</span>
          <input type="file" accept=".csv,.tsv,.txt,text/csv,text/plain"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              setPreview(null); setError(null);
              if (!file) { setCsv(''); setFileName(''); return; }
              setFileName(file.name);
              setCsv(await readReport(file));
            }} /></label>

        <label><span>Count as attended after</span>
          <span className="withhint">
            <input type="number" min={0} max={1440} className="narrow" value={minimumMinutes}
              onChange={(e) => setMinimum(e.target.value)} />
            <em>minutes in the meeting — 0 means anyone who joined</em>
          </span></label>

        <label className="check">
          <input type="checkbox" checked={markOthersAbsent}
            onChange={(e) => setMarkAbsent(e.target.checked)} />
          Mark everyone else on the course absent
        </label>

        {error && <p className="auth__error">{error}</p>}

        <div className="row">
          <button className="btn btn--primary" disabled={busy || !csv} onClick={() => void look()}>
            {busy ? 'Reading…' : 'See what it found'}
          </button>
          {fileName && <span className="muted small">{fileName}</span>}
        </div>
      </div>

      {preview && (
        <div className="panel">
          <p>
            <strong>{preview.linesRead}</strong> lines read.{' '}
            <strong>{preview.present.length}</strong> matched to trainees
            {preview.tooShort.length > 0 && <>, <strong>{preview.tooShort.length}</strong> there too briefly</>}
            {preview.unmatched.length > 0 && <>, <strong>{preview.unmatched.length}</strong> not recognised</>}.
          </p>

          {!preview.hasDurations && Number(minimumMinutes) > 0 && (
            <p className="warn">
              That file has no duration column, so the minimum time cannot be applied — everyone who appears
              will be marked present.
            </p>
          )}

          {preview.present.length > 0 && (
            <>
              <h3 className="h3">Present</h3>
              <ul className="plain">
                {preview.present.map((p) => (
                  <li key={p.userId}>
                    {p.name}
                    <span className="muted small">
                      {p.minutes != null ? ` · ${p.minutes} min` : ''} · matched by {p.by}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {preview.tooShort.length > 0 && (
            <>
              <h3 className="h3">There, but under {minimumMinutes} minutes</h3>
              <ul className="plain">
                {preview.tooShort.map((p) => (
                  <li key={p.userId}>{p.name} <span className="muted small">· {p.minutes ?? 0} min</span></li>
                ))}
              </ul>
            </>
          )}

          {preview.unmatched.length > 0 && (
            <>
              <h3 className="h3">Not recognised — mark these by hand</h3>
              <ul className="plain">
                {preview.unmatched.map((u, i) => (
                  <li key={i}>
                    <strong>{u.name || u.email}</strong>
                    <span className="muted small"> · {u.reason}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="row">
            <button className="btn btn--primary" disabled={busy} onClick={() => void apply()}>
              {busy ? 'Saving…' : `Take the register (${preview.present.length} present${markOthersAbsent ? `, ${preview.absent.length + preview.tooShort.length} absent` : ''})`}
            </button>
            <span className="muted small">Present completes the unit; absent fails it.</span>
          </div>
        </div>
      )}
    </section>
  );
}
